// One user's activity, opened from the Users admin page: what they have put
// through Hiring in a date range — job descriptions, candidates, assessments
// and offers — laid out as the other EquaOS apps are (owner, 29 Oct 2026):
// a header with the range and Reload, three cards of figures, the workflow
// with the stage picked open beside it, then the roles as one table.
//
// Data: GET /api/admin/users/:id/activity?startDate=&endDate=
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button, Card, EmptyState, Icon, Input, Select, StatusPill, Table, useShellLayout } from '../ds';
import { Page, PageHeader, Section, Loading, Note } from '../components/page';
const fmt   = n => (n ?? 0);
const pct   = (a, b) => (!b ? 0 : Math.round((a / b) * 100));
const avg   = arr => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : null;
// ── Metric row — label on the left, the figure on the right ──────────────────
function MetricBlock({ label, value, unit = '', sub }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
        <span className="num" style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-1)' }}>{value}</span>
        {unit && <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>{unit}</span>}
        {sub && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', marginLeft: 4 }}>{sub}</span>}
      </span>
    </div>
  );
}
// ── Main ──────────────────────────────────────────────────────────────────────
// ── Date range presets ────────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: 'Today',         days: 0   },
  { label: 'Last 7 Days',   days: 7   },
  { label: 'Last 30 Days',  days: 30  },
  { label: 'Last 3 Months', days: 90  },
  { label: 'Last 6 Months', days: 180 },
  { label: 'Last 12 Months',days: 365 },
  { label: 'All Time',      days: null },
  { label: 'Custom',        days: -1  },
];
function getDateRange(preset) {
  if (!preset || preset.days === null) return { startDate: null, endDate: null };
  if (preset.days === -1) return null; // custom — caller handles
  const end   = new Date();
  const start = new Date();
  if (preset.days === 0) {
    start.setHours(0, 0, 0, 0);
  } else {
    start.setDate(start.getDate() - preset.days);
  }
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}
export default function UserActivityDashboard({ userId, userName, backAction, notice }) {
  const layout = useShellLayout();
  const mobile = Boolean(layout && layout.mobile);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [company]                 = useState('all');
  const [companyId]               = useState('all');
  const [activeStage, setActiveStage] = useState(null);
  const [preset, setPreset]       = useState(DATE_PRESETS[2]);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  // Separate companies list — fetched once, never lost on filter change
  const navigate = useNavigate();
  // Core load — always takes explicit values to avoid stale closure issues
  const loadWith = async ({ pArg, cidArg, startArg, endArg } = {}) => {
    if (!userId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startArg) params.append('startDate', startArg);
      if (endArg)   params.append('endDate',   endArg);
      if (cidArg && cidArg !== 'all') params.append('companyId', cidArg);
      const r = await api.get(`/admin/users/${userId}/activity?${params.toString()}`);
      setData(r.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };
  // Helper: compute date range from preset + custom dates
  const currentDates = (p = preset) => {
    if (p.days === -1) return { startArg: customStart || null, endArg: customEnd || null };
    const range = getDateRange(p);
    return { startArg: range?.startDate || null, endArg: range?.endDate || null };
  };
  // Public reload — uses current state
  const load = () => loadWith({ cidArg: companyId, ...currentDates() });
  useEffect(() => { loadWith({ ...currentDates(DATE_PRESETS[2]) }); }, [userId]); // eslint-disable-line
  const handlePresetChange = (p) => {
    setPreset(p);
    if (p.days !== -1) {
      const { startArg, endArg } = currentDates(p);
      loadWith({ cidArg: companyId, startArg, endArg });
    }
  };
  // fc still used for client-side filtering of stageDetails (company filter on name)
  const fc = arr => company === 'all' ? (arr || []) : (arr || []).filter(i => i.companyName === company);
  // stageDetail must be declared before early returns (hooks rule)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stageDetail = useMemo(() => {
    if (!data || !activeStage || activeStage === 'assessment') return null;
    const sd2     = data.stageDetails || {};
    const jSummary = data.jobSummary  || {};
    const rr      = fc(sd2.resumeScreening || []);
    const oo      = fc(sd2.offers         || []);
    const tJDs    = jSummary.total ?? data.statistics?.totalJobs ?? 0;
    const sl      = rr.filter(r => r.status === 'shortlisted').length;
    const rej     = rr.filter(r => r.status === 'rejected').length;
    const sent    = oo.filter(o => ['sent','accepted'].includes(o.status)).length;
    const acc     = oo.filter(o => o.status === 'accepted').length;
    const map = {
      jd:      { title: 'Job Descriptions', items: [], pills: [
        { label: 'Total',       value: tJDs,                        tone: 'neutral'   },
        { label: 'In-Progress', value: jSummary.inProgress ?? 0,   tone: 'info' },
        { label: 'Completed',   value: jSummary.completed  ?? 0,   tone: 'success' },
      ]},
      media:   { title: 'Media & Content', items: [], pills: [
        { label: 'With Content', value: jSummary.withMediaPlan ?? 0,          tone: 'neutral' },
        { label: 'Total JDs',    value: tJDs,                                  tone: 'neutral'    },
        { label: 'Without',      value: tJDs - (jSummary.withMediaPlan ?? 0), tone: 'neutral'    },
      ]},
      resumes: { title: 'Resumes', items: rr, pills: [
        { label: 'Total',       value: rr.length, tone: 'neutral'   },
        { label: 'Shortlisted', value: sl,        tone: 'success' },
        { label: 'Rejected',    value: rej,       tone: 'danger'         },
      ]},
      offers:  { title: 'Offer Letters', items: oo, pills: [
        { label: 'Generated', value: oo.filter(o=>o.status==='generated').length, tone: 'neutral'   },
        { label: 'Sent',      value: sent,                                         tone: 'info' },
        { label: 'Accepted',  value: acc,                                          tone: 'success' },
      ]},
    };
    return map[activeStage] || null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage, data, company]);
  // The header: who, the range, and Reload. Shown while loading too.
  const header = (
    <PageHeader
      title={`Activity: ${userName}`}
      subtitle={`${preset.days === -1 ? 'Custom range' : preset.label} · Delhi Public School Pune East`}
      actions={(
        <>
          {backAction}
          {/* Date range presets */}
          <div style={{ width: 180 }}>
            <Select
              value={preset.label}
              onChange={(label) => {
                const p = DATE_PRESETS.find(d => d.label === label);
                if (p) handlePresetChange(p);
              }}
              options={DATE_PRESETS.map(p => ({ value: p.label, label: p.label }))}
            />
          </div>
          {/* Custom date inputs */}
          {preset.days === -1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 160 }}><Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} /></div>
              <span style={{ color: 'var(--text-muted)' }}>→</span>
              <div style={{ width: 160 }}><Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></div>
              <Button variant="secondary" onClick={() => load()}>Apply</Button>
            </div>
          )}
          {/* One school, one dashboard — the company filter is retired with
              the rest of the multi-company skin. */}
          {/* Manual reload button */}
          <Button variant="secondary" icon={<Icon name="arrow-clockwise" spin={loading} />} onClick={() => load()} disabled={loading}>
            {loading ? 'Loading…' : 'Reload'}
          </Button>
        </>
      )}
    />
  );
  if (loading) return <Page>{header}{notice}<Card><Loading /></Card></Page>;
  if (error) return <Page>{header}{notice}<Note tone="danger">{error}</Note></Page>;
  if (!data) return null;

  const { statistics, stageDetails: sd = {}, jobSummary = {}, pipelineStages = [] } = data;
  const resumes   = fc(sd.resumeScreening   || []);
  const preS      = fc(sd.preScreening      || []);
  const psycho    = fc(sd.psychoanalytical  || []);
  const finals    = fc(sd.finalInterview    || []);
  const offers    = fc(sd.offers            || []);
  const totalJDs      = jobSummary.total      ?? statistics?.totalJobs ?? 0;
  const inProgressJDs = jobSummary.inProgress ?? statistics?.activeJobs ?? 0;
  const shortlisted   = resumes.filter(r => r.status === 'shortlisted').length;
  const rejected      = resumes.filter(r => r.status === 'rejected').length;
  const offersSent    = offers.filter(o => ['sent','accepted'].includes(o.status)).length;
  const accepted      = offers.filter(o => o.status === 'accepted').length;
  const offerAccRate  = pct(accepted, offersSent);
  const shortlistRate = pct(shortlisted, resumes.length);
  // ── 5-stage workflow funnel ──────────────────────────────────────────────
  const totalAssessmentCandidates = preS.length + psycho.length + finals.length +
    pipelineStages.reduce((s, p) => s + (p.total || 0), 0);
  const workflowFunnel = [
    { key: 'jd',          label: 'Job Descriptions',  count: totalJDs },
    { key: 'media',       label: 'Media & Content',   count: jobSummary.withMediaPlan ?? 0 },
    { key: 'assessment',  label: 'Assessment',        count: totalAssessmentCandidates },
    { key: 'resumes',     label: 'Resumes',           count: resumes.length },
    { key: 'offers',      label: 'Offer Letter',      count: offers.length },
  ];
  // ── Assessment pass rates for bar chart — legacy + all custom pipeline stages
  const legacyAssessments = [
    { name: 'Pre-Screening',    total: preS.length,   passed: preS.filter(r => r.status==='shortlisted').length,   avgSc: avg(preS.filter(r=>r.score).map(r=>r.score)) },
    { name: 'Psychoanalytical', total: psycho.length, passed: psycho.filter(r => r.status==='shortlisted').length, avgSc: avg(psycho.filter(r=>r.score).map(r=>r.score)) },
    { name: 'Final Interview',  total: finals.length, passed: finals.filter(r => r.status==='shortlisted').length, avgSc: avg(finals.filter(r=>r.score).map(r=>r.score)) },
  ].filter(s => s.total > 0);
  // UUID pattern — entries whose label is still a raw UUID are noise, skip them
  const isUUID = str => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str?.trim() || '');
  // Include ALL configured pipeline stages (even those with 0 candidates — show as configured)
  const pipelineAssessments = pipelineStages
    .filter(s => s.label && !isUUID(s.label))   // drop UUID-labelled entries
    .map(s => ({
      name:    s.label,
      total:   s.total,
      passed:  s.passed,
      avgSc:   s.avgScore,
      invited: s.invited,
    }));
  // Deduplicate by label — if legacy and pipeline stage share the same name, merge them
  const assessmentMap = {};
  [...legacyAssessments, ...pipelineAssessments].forEach(a => {
    if (!a.name || isUUID(a.name)) return; // skip UUID-named entries
    const key = a.name.trim().toLowerCase();
    if (!assessmentMap[key]) {
      assessmentMap[key] = { ...a };
    } else {
      // Merge: sum totals, take max avgSc
      assessmentMap[key].total   = (assessmentMap[key].total  || 0) + (a.total  || 0);
      assessmentMap[key].passed  = (assessmentMap[key].passed || 0) + (a.passed || 0);
      if (a.avgSc != null) assessmentMap[key].avgSc = a.avgSc;
    }
  });
  // Only show stages that have actually been used (total > 0)
  const assessmentChart = Object.values(assessmentMap).filter(a => a.total > 0);
  // Pipeline table — use jobsDetail from backend (5 workflow stages per job)
  const pipelineRows = (data?.jobsDetail || []).sort((a, b) => b.resumes - a.resumes);
  const twoCols = mobile ? '1fr' : 'minmax(0,2fr) minmax(0,3fr)';
  const stageTone = (status) => (
    status === 'shortlisted' || status === 'accepted' ? 'success'
      : status === 'rejected' ? 'danger' : 'neutral'
  );
  const dash = <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const pipelineColumns = [
    { key: 'title', label: 'Role', render: (row) => <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{row.title}</span> },
    { key: 'status', label: 'JD', render: (row) => (
      <StatusPill tone={row.status === 'In-Progress' ? 'info' : row.status === 'Completed' ? 'success' : 'neutral'}>{row.status}</StatusPill>
    ) },
    { key: 'media', label: 'Media & Content', render: (row) => (row.hasMediaPlan ? <StatusPill tone="info" dot={false}>✓</StatusPill> : dash) },
    { key: 'assessment', label: 'Assessment', render: (row) => (row.assessment > 0 ? <span className="num" style={{ fontWeight: 600 }}>{row.assessment}</span> : dash) },
    { key: 'resumes', label: 'Resumes', render: (row) => (row.resumes > 0 ? <span className="num" style={{ fontWeight: 600 }}>{row.resumes}</span> : dash) },
    { key: 'offers', label: 'Offer Letter', render: (row) => (row.offers > 0 ? <span className="num" style={{ fontWeight: 600 }}>{row.offers}</span> : dash) },
  ];
  const linkStyle = {
    appearance: 'none', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
    color: 'var(--brand-primary-ink)', fontWeight: 500,
  };
  return (
    <Page>
      {header}
      {notice}
      {/* ── Row 1: 3 metric panels ── */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit,minmax(260px,1fr))', gap: mobile ? 16 : 24 }}>
        {/* JD Metrics */}
        <Section title="Job Descriptions">
          <MetricBlock value={fmt(totalJDs)}      label="Total JDs" />
          <MetricBlock value={fmt(inProgressJDs)} label="Active / Hiring" />
          <MetricBlock value={fmt(jobSummary.completed ?? 0)} label="Completed" />
          <MetricBlock value={fmt(jobSummary.withPipeline ?? 0)} label="With Pipeline" />
          <div style={{ marginTop: 12 }}>
            <Button size="sm" variant="ghost" icon={<Icon name="briefcase" />} onClick={() => navigate('/jobs')}>View all JDs →</Button>
          </div>
        </Section>
        {/* Candidate Metrics */}
        <Section title="Candidate Pipeline">
          <MetricBlock value={fmt(resumes.length)} label="Total Applications" />
          <MetricBlock value={fmt(shortlisted)}     label="Shortlisted" />
          <MetricBlock value={`${shortlistRate}`}   unit="%" label="Shortlist Rate" />
          <MetricBlock value={fmt(rejected)}         label="Rejected" />
        </Section>
        {/* Offer Metrics */}
        <Section title="Offers & Acceptance">
          <MetricBlock value={fmt(offers.length)} label="Total Offers" />
          <MetricBlock value={fmt(offersSent)}     label="Offers Sent" />
          <MetricBlock value={fmt(accepted)}       label="Accepted" />
          <MetricBlock value={`${offerAccRate}`}   unit="%" label="Acceptance Rate" />
        </Section>
      </div>
      {/* ── Row 2: Workflow Funnel (5 stages) + Assessment drill-down ── */}
      <div style={{ display: 'grid', gridTemplateColumns: twoCols, gap: mobile ? 16 : 24 }}>
        {/* Workflow Funnel */}
        <Section title="Hiring Workflow" subtitle="Click a stage to explore details">
          <div style={{ display: 'grid', gap: 14 }}>
            {workflowFunnel.map((stage) => {
              const w = pct(stage.count, workflowFunnel[0].count);
              const isActive = activeStage === stage.key;
              return (
                <div key={stage.key}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6, fontSize: 'var(--fs-body-sm)' }}>
                    <button
                      onClick={() => setActiveStage(isActive ? null : stage.key)}
                      aria-pressed={isActive}
                      style={{ ...linkStyle, display: 'flex', alignItems: 'center', gap: 6, color: isActive ? 'var(--brand-primary-ink)' : 'var(--text-1)', fontWeight: isActive ? 600 : 500 }}
                    >
                      {stage.label}
                      {stage.key === 'assessment' && assessmentChart.length > 0 && (
                        <StatusPill tone="neutral" dot={false}>{assessmentChart.length} stages</StatusPill>
                      )}
                    </button>
                    <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{fmt(stage.count)}</span>
                  </div>
                  <button
                    onClick={() => setActiveStage(isActive ? null : stage.key)}
                    aria-label={stage.label}
                    style={{ appearance: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'block', width: '100%', height: 8, background: 'var(--chart-track)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}
                  >
                    <div style={{ height: 8, borderRadius: 'var(--radius-pill)', width: `${Math.max(4, w)}%`, background: 'var(--chart-1)', opacity: isActive || !activeStage ? 1 : 0.55, transition: 'width var(--duration-fast)' }} />
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
            <span>JD → Offer conversion</span>
            <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>
              {pct(accepted, totalJDs)}%
            </span>
          </div>
        </Section>
        {/* Assessment drill-down OR stage detail */}
        <Section title={activeStage === 'assessment' ? 'Assessment Usage' : stageDetail?.title ?? 'Stage Detail'}>
          {!activeStage ? (
            <EmptyState
              icon={<Icon name="clipboard-text" size={36} />}
              title="Click a funnel stage to explore"
              description="Assessment stage shows all types and usage rates"
            />
          ) : activeStage === 'assessment' ? (
            /* Assessment breakdown */
            <div style={{ display: 'grid', gap: 12 }}>
              {assessmentChart.length === 0 ? (
                <EmptyState title="No assessments configured yet" />
              ) : assessmentChart.map((a, i) => {
                const usageRate = pct(a.total, resumes.length);
                const passRate  = pct(a.passed, a.total);
                return (
                  <div key={i} style={{ padding: 14, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface-1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 'var(--fs-body-sm)' }}>{a.name}</span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <StatusPill tone="neutral" dot={false}>{a.total} used</StatusPill>
                        <StatusPill tone="info" dot={false}>{usageRate}% usage</StatusPill>
                      </span>
                    </div>
                    <div style={{ height: 8, background: 'var(--chart-track)', borderRadius: 'var(--radius-pill)', overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ height: 8, borderRadius: 'var(--radius-pill)', background: 'var(--chart-1)', width: `${usageRate}%` }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>
                      <span>{a.passed} passed · {a.total - a.passed - (a.invited ?? 0)} pending</span>
                      <span style={{ fontWeight: 600, color: passRate >= 70 ? 'var(--status-success-text)' : passRate >= 50 ? 'var(--text-2)' : 'var(--status-danger-text)' }}>
                        {passRate}% pass rate
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Other stage detail: figures + candidate list */
            <div style={{ display: 'grid', gap: 14 }}>
              {/* Stage figures */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12 }}>
                {stageDetail?.pills?.map((p, i) => (
                  <div key={i} style={{ borderRadius: 'var(--radius-md)', padding: 12, textAlign: 'center', background: `var(--status-${p.tone}-bg)`, color: `var(--status-${p.tone}-text)` }}>
                    <div className="num" style={{ fontSize: 22, fontWeight: 600 }}>{fmt(p.value)}</div>
                    <div style={{ fontSize: 'var(--fs-caption)', marginTop: 2 }}>{p.label}</div>
                  </div>
                ))}
              </div>
              {/* Candidate list */}
              {activeStage === 'jd' || activeStage === 'media' ? (
                <p style={{ margin: 0, fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
                  {activeStage === 'jd' ? 'Candidate data is tied to individual JDs.' : 'Content is generated per JD from the Media Plan & Content tab.'}
                  <button onClick={() => navigate('/jobs')} style={{ ...linkStyle, marginLeft: 4 }}>View Jobs →</button>
                </p>
              ) : stageDetail?.items?.length > 0 ? (
                <>
                  <div style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-muted)' }}>Candidates</div>
                  <div style={{ maxHeight: 208, overflowY: 'auto' }}>
                    {stageDetail.items.slice(0, 25).map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.candidate}</div>
                          <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.jobTitle}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                          {r.score > 0 && <span className="num" style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' }}>{Math.round(r.score)}%</span>}
                          <StatusPill tone={stageTone(r.status)}>{r.status?.replace(/_/g,' ')}</StatusPill>
                        </div>
                      </div>
                    ))}
                    {stageDetail.items.length > 25 && (
                      <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', textAlign: 'center' }}>+{stageDetail.items.length - 25} more</p>
                    )}
                  </div>
                </>
              ) : (
                <EmptyState title="No candidates yet in this stage" />
              )}
            </div>
          )}
        </Section>
      </div>
      {/* ── Bottom: Current Hiring Pipeline full width ── */}
      <Section title="Current Hiring Pipeline" subtitle={`${pipelineRows.length} role${pipelineRows.length === 1 ? '' : 's'}`} padding="20px 0 8px">
        {pipelineRows.length === 0 ? (
          <EmptyState icon={<Icon name="users" size={36} />} title="No pipeline data yet" />
        ) : (
          <Table columns={pipelineColumns} rows={pipelineRows.map((row, i) => ({ ...row, _row: i }))} getRowId={(row) => row._row} stacked={mobile} />
        )}
      </Section>
    </Page>
  );
}
